import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { usePageTransition } from '../../utils/pageTransitions';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';

import Button from '../../components/ui/Button';
import ServiceCard from './components/ServiceCard';
import ServiceFilter from './components/ServiceFilter';
import ServiceComparison from './components/ServiceComparison';
import PackageConfigurator from './components/PackageConfigurator';
import PricingCalculator from './components/PricingCalculator';
import IndustryFilter from './components/IndustryFilter';
import ServiceQuoteModal from './components/ServiceQuoteModal';
import { serviceService } from '../../services/serviceService';

const ServicesOverview = () => {
  const { variants, transition } = usePageTransition();
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [selectedBusinessSize, setSelectedBusinessSize] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showComparison, setShowComparison] = useState(false);
  const [comparisonList, setComparisonList] = useState([]);
  const [showConfigurator, setShowConfigurator] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [activeTab, setActiveTab] = useState('services');
  const [showQuoteModal, setShowQuoteModal] = useState(false);

  useEffect(() => {
    loadServices();

    // Subscribe to real-time changes
    const unsubscribe = serviceService?.subscribeToChanges((payload) => {
      if (payload?.eventType === 'INSERT') {
        setServices(prev => [convertService(payload?.new), ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        setServices(prev => prev?.map(svc =>
          svc?.id === payload?.new?.id ? convertService(payload?.new) : svc
        ));
      } else if (payload?.eventType === 'DELETE') {
        setServices(prev => prev?.filter(svc => svc?.id !== payload?.old?.id));
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [services, selectedCategory, selectedIndustry, selectedBusinessSize, searchQuery]);

  const loadServices = async () => {
    try {
      setLoading(true);
      const data = await serviceService?.getAll();
      setServices(data);
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setLoading(false);
    }
  };

  const convertService = (svc) => ({
    id: svc?.id,
    title: svc?.title,
    category: svc?.category,
    icon: svc?.icon,
    image: svc?.image,
    imageAlt: svc?.image_alt,
    description: svc?.description,
    fullDescription: svc?.full_description,
    features: svc?.features || [],
    deliveryTime: svc?.delivery_time,
    startingPrice: svc?.starting_price,
    basePrice: svc?.base_price,
    industries: svc?.industries || [],
    businessSizes: svc?.business_sizes || [],
  });

  const applyFilters = () => {
    let filtered = [...services];

    if (selectedCategory !== 'all') {
      filtered = filtered?.filter(s => s?.category === selectedCategory);
    }

    if (selectedIndustry !== 'all') {
      filtered = filtered?.filter(s =>
        s?.industries?.includes('all') || s?.industries?.includes(selectedIndustry)
      );
    }

    if (selectedBusinessSize !== 'all') {
      filtered = filtered?.filter(s =>
        s?.businessSizes?.includes('all') || s?.businessSizes?.includes(selectedBusinessSize)
      );
    }

    if (searchQuery) {
      filtered = filtered?.filter(s =>
        s?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        s?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    setFilteredServices(filtered);
  };

  const handleResetFilters = () => {
    setSelectedCategory('all');
    setSelectedIndustry('all');
    setSelectedBusinessSize('all');
    setSearchQuery('');
  };

  const handleAddToComparison = (service) => {
    setComparisonList(prev => [...prev, service]);
  };

  const handleConfigure = (service) => {
    setSelectedService(service);
    setShowConfigurator(true);
  };

  const handleCalculate = (service) => {
    setSelectedService(service);
    setShowCalculator(true);
  };

  const handleRequestQuote = (service) => {
    setSelectedService(service);
    setShowQuoteModal(true);
  };

  const handleQuoteComplete = () => {
    setShowQuoteModal(false);
    setSelectedService(null);
  };

  const tabs = [
    { id: 'services', label: 'Tous les services', icon: 'Grid3x3' },
    { id: 'comparison', label: 'Comparaison', icon: 'GitCompare' },
    { id: 'packages', label: 'Forfaits', icon: 'Package' },
    { id: 'calculator', label: 'Calculateur', icon: 'Calculator' }];

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-white dark:bg-slate-900">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-2 border-blue-500 border-t-transparent mb-4" />
            <p className="text-slate-500 dark:text-slate-400">Chargement des services...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Services Overview - ORAZEK Investment | Solutions d'Entreprise Complètes en RDC</title>
        <meta name="description" content="Découvrez nos 12 services professionnels : conseil, juridique, immobilier, IT, logistique, événementiel et plus. Solutions complètes pour entreprises en RDC." />
      </Helmet>
      <Header />
      <motion.main
        className="flex-1 bg-white dark:bg-slate-900"
        initial="initial"
        animate="animate"
        exit="exit"
        variants={variants}
        transition={transition}
      >
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 overflow-hidden">
          {/* Noise texture */}
          <div className="absolute inset-0 opacity-10 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] brightness-100 contrast-150 mix-blend-overlay"></div>

          {/* Animated blobs */}
          <motion.div
            className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-blue-500/15 blur-[150px]"
            animate={{ scale: [1, 1.15, 1], opacity: [0.15, 0.25, 0.15] }}
            transition={{ duration: 10, repeat: Infinity }}
          />
          <motion.div
            className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-amber-500/10 blur-[120px]"
            animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
            transition={{ duration: 8, repeat: Infinity, delay: 2 }}
          />

          <div className="relative w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12 lg:mb-16"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/15 px-5 py-2.5 rounded-full mb-6"
              >
                <Icon name="Sparkles" size={18} className="text-blue-400" />
                <span className="text-sm font-medium text-blue-200">12 Services Premium</span>
              </motion.div>

              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6" style={{ fontFamily: 'var(--font-headline)' }}>
                Nos Services{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-amber-400">
                  d'Entreprise
                </span>
              </h1>
              <p className="text-lg md:text-xl text-blue-100/70 max-w-3xl mx-auto">
                Solutions complètes et professionnelles pour accompagner votre croissance en République Démocratique du Congo
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6"
            >
              {[
                { icon: 'Award', label: '12+', value: 'Services Premium' },
                { icon: 'Users', label: '500+', value: 'Clients Satisfaits' },
                { icon: 'TrendingUp', label: '98%', value: 'Satisfaction' },
                { icon: 'Clock', label: '24/7', value: 'Support' }
              ]?.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-5 md:p-6 text-center hover:border-blue-400/30 transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg shadow-blue-900/30">
                    <Icon name={stat?.icon} size={24} className="text-white" />
                  </div>
                  <p className="text-2xl md:text-3xl font-bold text-white mb-1">{stat?.label}</p>
                  <p className="text-sm text-blue-200/60">{stat?.value}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Tabs & Content Section */}
        <section className="py-12 lg:py-20 bg-white dark:bg-slate-900">
          <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16">
            {/* Tab Navigation */}
            <div className="flex gap-2 overflow-x-auto pb-4 mb-8 lg:mb-10 scrollbar-hide border-b border-slate-200 dark:border-white/10">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-5 md:px-6 py-2.5 md:py-3 rounded-xl font-medium transition-all flex-shrink-0 ${
                    activeTab === tab?.id
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25'
                      : 'bg-slate-100 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-white/5'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span className="text-sm md:text-base whitespace-nowrap">{tab?.label}</span>
                </button>
              ))}
            </div>

            {activeTab === 'services' && (
              <>
                <ServiceFilter
                  selectedCategory={selectedCategory}
                  setSelectedCategory={setSelectedCategory}
                  selectedIndustry={selectedIndustry}
                  setSelectedIndustry={setSelectedIndustry}
                  selectedSize={selectedBusinessSize}
                  setSelectedSize={setSelectedBusinessSize}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  onReset={handleResetFilters}
                />

                <IndustryFilter
                  selectedIndustry={selectedIndustry}
                  setSelectedIndustry={setSelectedIndustry}
                />

                <div className="flex items-center justify-between mb-6">
                  <p className="text-sm md:text-base text-slate-600 dark:text-slate-400">
                    {filteredServices?.length} service{filteredServices?.length !== 1 ? 's' : ''} trouvé{filteredServices?.length !== 1 ? 's' : ''}
                  </p>
                  {filteredServices?.length === 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="RotateCcw"
                      iconPosition="left"
                      onClick={handleResetFilters}
                    >
                      Réinitialiser les filtres
                    </Button>
                  )}
                </div>

                {filteredServices?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredServices?.map((service, index) => (
                      <ServiceCard
                        key={service?.id}
                        service={service}
                        index={index}
                        onRequestQuote={handleRequestQuote}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <div className="w-20 h-20 bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-white/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <Icon name="Search" size={32} className="text-slate-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">Aucun service trouvé</h3>
                    <p className="text-slate-600 dark:text-slate-400 mb-6">Essayez de modifier vos critères de recherche</p>
                    <Button
                      variant="outline"
                      iconName="RotateCcw"
                      iconPosition="left"
                      onClick={handleResetFilters}
                      className="border-slate-300 dark:border-white/20 text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-white/5"
                    >
                      Réinitialiser les filtres
                    </Button>
                  </div>
                )}
              </>
            )}

            {activeTab === 'comparison' && (
              <ServiceComparison services={services} />
            )}

            {activeTab === 'packages' && (
              <PackageConfigurator services={services} />
            )}

            {activeTab === 'calculator' && (
              <PricingCalculator />
            )}
          </div>
        </section>

        {/* Why Choose ORAZEK Section */}
        <section className="relative py-20 lg:py-28 bg-white dark:bg-slate-900 overflow-hidden">
          {/* Noise texture */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`, backgroundRepeat: 'repeat' }} />

          <div className="relative w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12 lg:mb-16"
            >
              <div className="inline-flex items-center gap-2 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-2 rounded-full mb-6">
                <Icon name="Shield" size={16} className="text-amber-400" />
                <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Notre Engagement</span>
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'var(--font-headline)' }}>
                Pourquoi choisir{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-amber-400">
                  ORAZEK ?
                </span>
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-400 max-w-3xl mx-auto">
                Votre partenaire de confiance pour tous vos besoins professionnels en RDC
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {[
                {
                  icon: 'Award',
                  title: 'Excellence Garantie',
                  description: 'Standards de qualité internationaux adaptés au marché congolais avec certification ISO'
                },
                {
                  icon: 'Users',
                  title: 'Équipe Expérimentée',
                  description: 'Professionnels qualifiés avec expertise locale et vision internationale'
                },
                {
                  icon: 'Clock',
                  title: 'Disponibilité 24/7',
                  description: 'Support client continu et réponse rapide à vos besoins urgents'
                },
                {
                  icon: 'Shield',
                  title: 'Conformité Totale',
                  description: 'Respect strict des réglementations congolaises et standards internationaux'
                },
                {
                  icon: 'TrendingUp',
                  title: 'Résultats Mesurables',
                  description: 'Approche orientée résultats avec KPIs clairs et reporting transparent'
                },
                {
                  icon: 'Heart',
                  title: 'Engagement Client',
                  description: 'Partenariat à long terme basé sur la confiance et la satisfaction mutuelle'
                }
              ]?.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white/80 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-3xl p-6 shadow-2xl hover:border-blue-500/30 transition-all duration-300"
                >
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center mb-4 shadow-lg shadow-blue-900/30">
                    <Icon name={benefit?.icon} size={26} className="text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">{benefit?.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">{benefit?.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-20 lg:py-28 bg-white dark:bg-slate-900 overflow-hidden">
          <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16">
            <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 rounded-[2.5rem] p-10 md:p-16 overflow-hidden">
              {/* CTA blobs */}
              <motion.div
                className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-white/5 blur-3xl"
                animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
                transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
              />
              <motion.div
                className="absolute bottom-0 left-0 w-[300px] h-[300px] rounded-full bg-amber-500/10 blur-3xl"
                animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
                transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
              />

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative text-center"
              >
                <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Icon name="Sparkles" size={32} className="text-white" />
                </div>
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-headline)' }}>
                  Prêt à transformer votre entreprise ?
                </h2>
                <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
                  Contactez-nous dès aujourd'hui pour une consultation gratuite et découvrez comment nos services peuvent propulser votre croissance
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      variant="default"
                      size="lg"
                      className="bg-white text-blue-700 hover:bg-white/90 shadow-2xl border-none rounded-xl px-8 py-3 font-semibold"
                      iconName="Calendar"
                      iconPosition="left"
                    >
                      Réserver une consultation
                    </Button>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      variant="outline"
                      size="lg"
                      className="border-white/30 text-white hover:bg-white/10 backdrop-blur-sm rounded-xl px-8 py-3 font-semibold"
                      iconName="Phone"
                      iconPosition="left"
                    >
                      Nous appeler
                    </Button>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </motion.main>
      <Footer />
      <ServiceQuoteModal
        isOpen={showQuoteModal}
        onClose={() => setShowQuoteModal(false)}
        service={selectedService}
        onComplete={handleQuoteComplete}
      />
    </>
  );

};

export default ServicesOverview;
